/**
 * HomeReviews — "WHAT RIDERS SAY" social proof section.
 * Maps to the review cards section in index.html.
 * Fetches from GET /api/home-reviews (public, 3 most recent approved reviews).
 * Returns whatever the API provides — never pads with fake data.
 * Design tokens: ds-surface-container-low (section bg), ds-primary (stars),
 * ds-on-background (text), ds-on-surface (card text), inter (body), barlow (heading).
 */
import { useEffect, useState } from "react";
import { FaRegStar, FaStar, FaStarHalfAlt } from "react-icons/fa";
import SummaryApi from "../common";
import { request } from "../helpers/request";

const ReviewCardSkeleton = () => (
  <div className="bg-white border border-ds-outline-variant/30 rounded-xl p-5 flex flex-col gap-3 animate-pulse">
    <div className="flex gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="w-4 h-4 bg-ds-surface-container rounded" />
      ))}
    </div>
    <div className="space-y-2">
      <div className="h-3 bg-ds-surface-container rounded w-full" />
      <div className="h-3 bg-ds-surface-container rounded w-4/5" />
      <div className="h-3 bg-ds-surface-container rounded w-3/5" />
    </div>
    <div className="space-y-1 mt-auto">
      <div className="h-3 bg-ds-surface-container rounded w-1/3" />
      <div className="h-3 bg-ds-surface-container rounded w-1/2" />
    </div>
  </div>
);

const Stars = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return (
    <div
      className="flex text-ds-primary"
      aria-label={`${rating} out of 5 stars`}
    >
      {Array.from({ length: full }, (_, i) => (
        <FaStar key={`f${i}`} className="text-[15px]" />
      ))}
      {half === 1 && <FaStarHalfAlt className="text-[15px]" />}
      {Array.from({ length: empty }, (_, i) => (
        <FaRegStar key={`e${i}`} className="text-[15px] opacity-40" />
      ))}
    </div>
  );
};

const HomeReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchReviews = async () => {
      try {
        const res = await request(SummaryApi.homeReviews);
        const json = await res.json();
        if (!cancelled && json.success) setReviews(json.data);
      } catch {
        // Section silently absent if API is unreachable
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReviews();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide section entirely when no approved reviews exist yet
  if (!loading && reviews.length === 0) return null;

  return (
    <section
      className="bg-ds-surface-container-low py-[80px] px-10"
      aria-label="Customer reviews"
    >
      <div className="max-w-[1280px] mx-auto">
        <h2 className="font-barlow font-bold text-[36px] text-ds-on-background mb-8 uppercase">
          WHAT RIDERS SAY
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {loading
            ? Array.from({ length: 3 }, (_, i) => (
                <ReviewCardSkeleton key={i} />
              ))
            : reviews.map((review, idx) => (
                <article
                  key={idx}
                  className="bg-white border border-ds-outline-variant/30 rounded-xl p-5 shadow-sm flex flex-col gap-3"
                >
                  <Stars rating={review.rating} />

                  <blockquote className="font-inter text-[14px] text-ds-on-surface leading-[1.7] line-clamp-3 flex-1">
                    "{review.comment}"
                  </blockquote>

                  <footer>
                    <p className="font-inter font-semibold text-[14px] text-ds-on-background">
                      {review.reviewerName}
                    </p>
                    {review.productName && (
                      <p className="font-inter text-ds-primary text-[12px] mt-0.5">
                        {review.productName}
                      </p>
                    )}
                  </footer>
                </article>
              ))}
        </div>

        {!loading && reviews.length > 0 && (
          <div className="text-center">
            <p className="font-inter font-medium text-[15px] text-ds-on-background">
              Trusted by riders across Nepal
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeReviews;
